'use no memo'

import {
  createPromoCampaign,
  exportUniqueCodesAsXlsx,
  type PromoCampaignFormOutput,
  updatePromoCampaign,
  usePromoCampaign,
  usePromoCampaignForm,
  zPromoCampaignForm,
} from '@gozoki/api'
import { CAMPAIGN_TARGETS, getStoreTargetRefs, getTargetIds } from '@gozoki/api-types'
import { asStringNumber } from '@gozoki/tools'
import { Alert, Button, IconButton, Typography } from '@mui/material'
import { useMutation, useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { ArrowLeft } from 'lucide-react'
import { useCallback, useEffect, useMemo } from 'react'
import { useWatch } from 'react-hook-form'
import z from 'zod/v3'
import { api } from '~/lib/tuyau'
import type { PermissionTuple } from '~/utils/ability'
import { useMigratedRight, withRoutePermissions } from '~/utils/rbac'
import { AuthorizedButton } from '../../../components/communication/AuthorizedButton'
import { useToast } from '../../../components/communication/Toast'
import LoadingPaper from '../../../components/containers/LoadingPaper'
import type { MenuOption } from '../../../components/containers/MenuOptions'
import DatePickerRhf from '../../../components/inputs/DatePickerRhf'
import RadioGroupRhf from '../../../components/inputs/RadioGroupRhf'
import SelectMultipleRhf from '../../../components/inputs/SelectMultipleRhf'
import SelectRhf from '../../../components/inputs/SelectRhf'
import TextFieldRhf from '../../../components/inputs/TextFieldRhf'
import Page from '../../../components/Page'
import { useProductSelectOptions } from '../../../utils/hooks/useProductSelectOptions'
import { getErrorMessageWithSentry } from '../../../utils/sentry'
import { ThemeColors } from '../../../utils/theme/colors.theme'
import { fonts } from '../../../utils/theme/fonts.theme'
import { ButtonOverrides, TextOverrides } from '../../../utils/theme/overrides.theme'

const ADD_CAMPAIGN: PermissionTuple = ['add', 'promo.temporary-campaigns.campaign']
const EDIT_CAMPAIGN: PermissionTuple = ['edit', 'promo.temporary-campaigns.campaign']

export const Route = createFileRoute('/_authenticated/promo-campaign/new')({
  staticData: {
    permission: ['read', 'promo.temporary-campaigns.campaign'],
  },
  component: AddPromoCampaignPage,
  validateSearch: z.object({
    promoCampaignId: z.number().optional(),
  }),
})
withRoutePermissions(Route)

const typeOptions: MenuOption[] = CAMPAIGN_TARGETS.filter((target) => target !== 'Menu').map(
  (target) => ({
    label: target,
    value: target,
  }),
)

// Une campagne à codes uniques n'a jamais de code public (refusé par le backend)
const toCampaignPayload = (values: PromoCampaignFormOutput): PromoCampaignFormOutput =>
  values.campaignType === 'unique' ? { ...values, publicCode: null } : values

function AddPromoCampaignPage() {
  const navigate = Route.useNavigate()
  const { promoCampaignId } = Route.useSearch()
  const updateMode = !!promoCampaignId

  // If this is update mode, use isLoading to show a loading indicator before the data arrives
  const { data, isLoading } = usePromoCampaign(promoCampaignId)

  const { control, trigger, watch, getValues, setValue } = usePromoCampaignForm()
  const type = useWatch({ control, name: 'type' })
  // `useWatch` et non `watch()` : avant le 'use no memo', le React Compiler mémoïsait
  // `watch('campaignType')` sur l'identité stable de `watch`. Le champ « code » restait affiché en
  // mode codes uniques, la remise à zéro ci-dessous ne partait jamais, et la saisie finissait en
  // code public de la campagne.
  const selectedCampaignType = useWatch({ control, name: 'campaignType' })

  const productOptions = useProductSelectOptions()
  // La liste complète des comptes exige le droit d'export clients (#1953) et laisse une trace
  // d'export : seul le ciblage « Utilisateur » s'en sert, elle n'est pas chargée à l'ouverture.
  const users = useQuery({
    ...api.users.indexApp.queryOptions({ query: { all: true } }, { select: (data) => data.items }),
    enabled: type === 'Utilisateur',
  })
  const [Toast, showToast] = useToast()

  const canEdit = useMigratedRight(EDIT_CAMPAIGN, ['ADMIN'])
  // Fill the form with initial data once the data is fetched
  useEffect(() => {
    // Set all values
    if (data) {
      let campaignType = data.isPublic ? 'public' : 'private'

      if (data.isUsingUniqueCodes) {
        campaignType = 'unique'
      }

      setValue('name', data.name)
      setValue('type', data.type)
      setValue('targetIds', getTargetIds(data))
      setValue('storeTargetRefs', getStoreTargetRefs(data))
      setValue('reductionType', data.reductionType)
      setValue('reductionValue', asStringNumber(data.reductionValue))
      setValue('startDate', data.startDate)
      setValue('endDate', data.endDate)
      setValue('campaignType', campaignType)
      setValue('publicCode', data.publicCode)
    }
  }, [setValue, data])

  const navigateBack = useCallback(() => {
    navigate({ to: '/promo-campaign' })
  }, [navigate])

  const userOptions: MenuOption[] = useMemo(() => {
    const baseUserChoices: MenuOption[] = []
    const allUsersIds: number[] = []
    users.data?.forEach((user) => {
      baseUserChoices.push({
        label: user.email,
        value: user.id,
      })
      allUsersIds.push(user.id)
    })
    const allChoices: MenuOption[] = []
    allChoices.push({
      isHelper: true,
      label: 'Tous les utilisateurs',
      value: allUsersIds,
    })
    baseUserChoices.forEach((user) => {
      allChoices.push(user)
    })
    return allChoices
  }, [users.data])

  /* NOTE: ATM campaigns are used for all the stores */
  // Const stores = useStores();

  // Const storesData = useMemo(
  //     () => stores.data?.filter((s) => s.storeType === 'BOUTIQUE') ?? [],
  //     [stores.data]
  // );

  // Const storeOptions: MenuOption[] = useMemo(() => {
  //     Const baseStoreChoices: MenuOption[] = [];
  //     Const allStoresRefs: string[] = [];
  //     StoresData.forEach((store) => {
  //         BaseStoreChoices.push({
  //             Label: store.label,
  //             Value: store.checkpointStoreRef,
  //         });
  //         AllStoresRefs.push(store.checkpointStoreRef);
  //     });
  //     Const allChoices: MenuOption[] = [];
  //     AllChoices.push({
  //         Label: 'Toutes les boutiques',
  //         Value: allStoresRefs,
  //         IsHelper: true,
  //     });
  //     BaseStoreChoices.forEach((store) => {
  //         AllChoices.push(store);
  //     });
  //     Return allChoices;
  // }, [storesData]);

  const reductionOptions: MenuOption[] = useMemo(() => {
    if (type === 'Produit') {
      return [
        // TODO : taux promo
        // { label: 'Taux', value: 'Taux' },
        { label: '1 acheté 1 offert', value: 'ONE_FOR_ONE' },
      ]
    }
    // TODO : user promo campaigns
    // If (type === 'Utilisateur') {
    //     Return [
    //         { label: 'Taux', value: 'Taux' },
    //         { label: 'BRI', value: 'BRI' },
    //     ];
    // }
    if (type === 'Panier') {
      return [{ label: 'BRI', value: 'BRI' }]
    }
    return []
  }, [type])

  const campaignTypeOptions: MenuOption[] = useMemo(() => {
    if (type === 'Produit' || type === 'Menu') {
      return [
        { label: 'Code Promo', value: 'private' },
        { label: 'Code Promo à Usage Unique', value: 'unique' },
      ]
    }
    if (type === 'Panier') {
      return [{ label: 'Code Promo à Usage Unique', value: 'unique' }]
    }
    if (type === 'Utilisateur') {
      return [{ label: 'Code Promo', value: 'private' }]
    }
    return []
  }, [type])

  // Display '%' or '€' depending on the reduction type
  const isRate = watch('reductionType') === 'Taux'

  // ******************************************************************** //
  //                              MUTATIONS                               //
  // ******************************************************************** //

  const { mutate: upload, isPending: isUploading } = useMutation({
    mutationFn: createPromoCampaign,
    onError: (error) => {
      showToast({
        severity: 'error',
        message: getErrorMessageWithSentry(error),
      })
    },
    onSuccess: navigateBack,
  })

  const { mutate: update, isPending: isUpdating } = useMutation({
    mutationFn: updatePromoCampaign,
    onError: (error) => {
      showToast({
        severity: 'error',
        message: getErrorMessageWithSentry(error),
      })
    },
    onSuccess: navigateBack,
  })

  const exportUniqueCodeXlsx = useMutation({
    mutationFn: exportUniqueCodesAsXlsx,
    onError: (error) => {
      showToast({
        severity: 'error',
        message: getErrorMessageWithSentry(error),
      })
    },
    onSuccess: async (dataOnSuccess) => {
      const url = window.URL.createObjectURL(dataOnSuccess)
      const link = document.createElement('a')
      link.href = url
      link.download = `codes.xlsx`
      link.click()
      URL.revokeObjectURL(url)
    },
  })

  const onClickExportUniqueCodeCsv = () => {
    if (!data) {
      return
    }

    exportUniqueCodeXlsx.mutate({ id: data.id })
  }

  // ******************************************************************** //
  //                               HANDLERS                               //
  // ******************************************************************** //

  const uploadCampaign = async () => {
    const valid = await trigger() // Trigger form validation

    if (type === 'Produit' && getValues('targetIds').length === 0) {
      showToast({
        message: 'Veuillez sélectionner au moins un produit',
        severity: 'error',
      })
      return
    }

    if (type === 'Utilisateur' && getValues('targetIds').length === 0) {
      showToast({
        message: 'Veuillez sélectionner au moins un utilisateur',
        severity: 'error',
      })
      return
    }

    if (valid) {
      upload(toCampaignPayload(zPromoCampaignForm.parse(getValues())))
    } else {
      showToast({
        message: 'Veuillez remplir tous les champs obligatoires',
        severity: 'error',
      })
    }
  }

  const updateCampaign = async () => {
    const valid = await trigger() // Trigger form validation

    if (type === 'Produit' && getValues('targetIds').length === 0) {
      showToast({
        message: 'Veuillez sélectionner au moins un produit',
        severity: 'error',
      })
      return
    }

    if (type === 'Utilisateur' && getValues('targetIds').length === 0) {
      showToast({
        message: 'Veuillez sélectionner au moins un utilisateur',
        severity: 'error',
      })
      return
    }

    if (valid && promoCampaignId) {
      update({
        id: promoCampaignId,
        // Call the zod parser in order to transform strings with French number notation into numbers
        data: toCampaignPayload(zPromoCampaignForm.parse(getValues())),
      })
    }
  }

  // If the user switch campaign type we need to resert targetIds

  useEffect(() => {
    if (data) {
      setValue('targetIds', getTargetIds(data))
    }
    // oxlint-disable-next-line exhaustive-deps
  }, [type, setValue])

  useEffect(() => {
    // I dont want to make reductionValue nullable so :
    if (getValues('reductionType') === 'ONE_FOR_ONE') {
      // @ts-ignore
      setValue('reductionValue', '0')
    }
    // oxlint-disable-next-line exhaustive-deps
  }, [getValues, setValue, watch('reductionType')])

  useEffect(() => {
    /* Reset fields when switching type */
    if (selectedCampaignType === 'unique') {
      setValue('quantityUniqueCode', '0')
      setValue('publicCode', '')
    } else {
      setValue('quantityUniqueCode', null)
    }
  }, [selectedCampaignType, setValue])

  return (
    <Page sx={{ margin: 'auto', maxWidth: '50vw' }}>
      <Toast />
      <div style={{ alignItems: 'center', display: 'flex' }}>
        <IconButton onClick={navigateBack}>
          <ArrowLeft
            style={{
              color: ThemeColors.BLACK,
              height: '24px',
              width: '24px',
            }}
          />
        </IconButton>
        <Typography style={{ ...fonts.pageSubtitle, marginLeft: '16px' }} flexGrow={1}>
          {updateMode
            ? `${canEdit ? 'Modifier une campagne' : 'Voir une campagne'}`
            : 'Ajouter une campagne'}
        </Typography>

        <Button
          variant="outlined"
          sx={{ marginRight: '16px', ...ButtonOverrides.blackOutlined }}
          onClick={navigateBack}
        >
          Annuler
        </Button>
        <AuthorizedButton
          permission={updateMode ? EDIT_CAMPAIGN : ADD_CAMPAIGN}
          legacyRights={['ADMIN']}
          variant="contained"
          disabled={isUpdating || isUploading}
          onClick={updateMode ? updateCampaign : uploadCampaign}
        >
          {updateMode ? 'Modifier' : 'Ajouter'}
        </AuthorizedButton>
      </div>
      <LoadingPaper
        sx={{ marginTop: '40px', padding: '24px', paddingBottom: '40px' }}
        loading={isLoading && updateMode}
      >
        <Typography mb="8px" style={fonts.inputHeader}>
          Nom
        </Typography>
        <TextFieldRhf
          variant="outlined"
          placeholder="Nom de la campagne"
          color="info"
          control={control}
          trigger={trigger}
          name="name"
          fullWidth
        />
        <Typography mb="8px" mt="24px" style={fonts.inputHeader}>
          Type
        </Typography>

        <SelectRhf
          name="type"
          options={typeOptions}
          control={control}
          sx={{ marginBottom: '16px' }}
          disableClearable
        />

        {type === 'Utilisateur' && (
          <Alert severity="error">
            Ce type de promo n'est pas encore disponible. Veuillez choisir un autre type.
          </Alert>
        )}

        {type === 'Produit' ? (
          <>
            <Typography mb="8px" mt="24px" style={fonts.inputHeader}>
              Produits
            </Typography>
            <SelectMultipleRhf options={productOptions} control={control} name="targetIds" />
          </>
        ) : null}

        {type === 'Utilisateur' ? (
          <>
            <Typography mb="8px" mt="24px" style={fonts.inputHeader}>
              Utilisateurs
            </Typography>
            <SelectMultipleRhf options={userOptions} control={control} name="targetIds" />
          </>
        ) : null}

        {/* NOTE: ATM campaigns are used for all the stores */}
        {/* <Typography mb="8px" mt="24px" style={fonts.inputHeader}>
                  Boutiques
              </Typography>
              <SelectMultipleRhf
                  disabled
                  options={storeOptions}
                  control={control}
                  name="storeTargetRefs"
              />
              <Alert severity="info">
                  Ettant donné qu'il n'y a qu'une seule boutique pour le moment, la sélection de
                  boutiques n'est pas encore disponible.
              </Alert> */}

        <Typography mb="8px" mt="24px" style={fonts.inputHeader}>
          Réduction
        </Typography>
        <div style={{ display: 'flex' }}>
          <RadioGroupRhf
            options={reductionOptions}
            control={control}
            name="reductionType"
            sx={{
              display: 'flex',
              flexDirection: 'row',
            }}
          />
          <div style={{ flex: 1 }} />
          {!(getValues('reductionType') === 'ONE_FOR_ONE') && (
            <TextFieldRhf
              variant="outlined"
              control={control}
              trigger={trigger}
              name="reductionValue"
              placeholder={isRate ? '%' : '€'}
              inputProps={{
                ...TextOverrides.rightPlaceholder,
                style: { width: '250px' },
              }}
            />
          )}
        </div>
        <Typography mb="8px" mt="24px" style={fonts.inputHeader}>
          Date d'effet
        </Typography>
        <div style={{ display: 'flex' }}>
          <DatePickerRhf label="du" control={control} name="startDate" before={watch('endDate')} />
          <div style={{ width: '16px' }} />
          <DatePickerRhf label="au" control={control} name="endDate" after={watch('startDate')} />
        </div>
        <Typography mb="8px" mt="24px" style={fonts.inputHeader}>
          Informations
        </Typography>
        <div style={{ display: 'flex' }}>
          <div style={{ flex: 1 }}>
            <RadioGroupRhf
              options={campaignTypeOptions}
              control={control}
              name="campaignType"
              sx={{
                display: 'flex',
                flexDirection: 'row',
              }}
              isDisabled={(data && data.isUsingUniqueCodes) ?? undefined}
            />
          </div>
          <div style={{ width: '16px' }} />
          {selectedCampaignType === 'private' ? (
            <div style={{ flex: 1 }}>
              <TextFieldRhf
                variant="outlined"
                control={control}
                trigger={trigger}
                name="publicCode"
                placeholder="code"
                fullWidth
              />
            </div>
          ) : null}
          {selectedCampaignType === 'unique' && !data ? (
            <div style={{ flex: 1 }}>
              <TextFieldRhf
                type="number"
                variant="outlined"
                control={control}
                trigger={trigger}
                name="quantityUniqueCode"
                placeholder="quantité"
                label="Quantité"
                fullWidth
              />
            </div>
          ) : null}
          {selectedCampaignType === 'unique' && data && (
            <AuthorizedButton
              permission={['export', 'promo.temporary-campaigns.single-use-codes']}
              onClick={onClickExportUniqueCodeCsv}
              disabled={exportUniqueCodeXlsx.isPending}
            >
              Export XLSX
            </AuthorizedButton>
          )}
        </div>
      </LoadingPaper>
    </Page>
  )
}
